import type { AxiosRequestConfig } from 'axios'

import type { ApiResponse, ApisauceInstance } from '@mangovoice/apisauce'
import type {
  ApiError,
  ExtensionInstance,
  MessageGroup,
  PaginatedApiResult,
  PhoneNumber,
} from '@mangovoice/types'

export interface SwitchboardListExtensionsParams {
  pbx_id: number
  type?: string
  limit?: number
  offset?: number
  ordering?: string
  voicemail_enabled?: boolean
}

export interface SwitchboardListMessageGroupsParams {
  pbx_id: number
  limit?: number
  offset?: number
  ordering?: string
}

export interface SwitchboardListPhoneNumbersParams {
  pbx_id: number
  limit?: number
  offset?: number
  search?: string
  sms_eligible?: boolean
}

export interface SwitchboardSchedule {
  id: number
  uuid?: string | null
  name: string
  pbx_id: number
  created_at?: string
  updated_at?: string
}

export interface SwitchboardSoundClip {
  id: number
  name: string
  feature_code?: number
  location?: string
  pbx_id: number
  pbx_uuid?: string
  created_at?: string
  updated_at?: string
}

export interface SwitchboardNamespace {
  request: {
    get: <T, E = ApiError>(
      url: string,
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig,
    ) => Promise<ApiResponse<T, E>>
    post: <T, E = ApiError>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ) => Promise<ApiResponse<T, E>>
    patch: <T, E = ApiError>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ) => Promise<ApiResponse<T, E>>
    put: <T, E = ApiError>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ) => Promise<ApiResponse<T, E>>
    delete: <T, E = ApiError>(
      url: string,
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig,
    ) => Promise<ApiResponse<T, E>>
  }
  listExtensions: (
    params: SwitchboardListExtensionsParams,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResponse<PaginatedApiResult<ExtensionInstance>, ApiError>>
  listMessageGroups: (
    params: SwitchboardListMessageGroupsParams,
  ) => Promise<ApiResponse<PaginatedApiResult<MessageGroup>, ApiError>>
  listPhoneNumbers: (
    params: SwitchboardListPhoneNumbersParams,
  ) => Promise<ApiResponse<PaginatedApiResult<PhoneNumber>, ApiError>>
  listSchedules: (params: {
    pbx_id: number
    limit?: number
    offset?: number
  }) => Promise<ApiResponse<PaginatedApiResult<SwitchboardSchedule>, ApiError>>
  listSoundClips: (params: {
    pbx_id: number
    limit?: number
    offset?: number
  }) => Promise<ApiResponse<PaginatedApiResult<SwitchboardSoundClip>, ApiError>>
}

export const createSwitchboardNamespace = ({
  withJwt,
  mangoApi,
}: {
  withJwt: (api: ApisauceInstance) => ApisauceInstance
  mangoApi: ApisauceInstance
}): SwitchboardNamespace => ({
  request: {
    get: <T, E = ApiError>(
      url: string,
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig,
    ): Promise<ApiResponse<T, E>> => {
      return withJwt(mangoApi).get<T, E>(url, params, config)
    },
    post: <T, E = ApiError>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<ApiResponse<T, E>> => {
      return withJwt(mangoApi).post<T, E>(url, data, config)
    },
    patch: <T, E = ApiError>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<ApiResponse<T, E>> => {
      return withJwt(mangoApi).patch<T, E>(url, data, config)
    },
    put: <T, E = ApiError>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<ApiResponse<T, E>> => {
      return withJwt(mangoApi).put<T, E>(url, data, config)
    },
    delete: <T, E = ApiError>(
      url: string,
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig,
    ): Promise<ApiResponse<T, E>> => {
      return withJwt(mangoApi).delete<T, E>(url, params, config)
    },
  },
  listExtensions: (
    params: SwitchboardListExtensionsParams,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<PaginatedApiResult<ExtensionInstance>, ApiError>> =>
    withJwt(mangoApi).get<PaginatedApiResult<ExtensionInstance>, ApiError>(
      '/app/v1/extensions/',
      params,
      config,
    ),
  listMessageGroups: (
    params: SwitchboardListMessageGroupsParams,
  ): Promise<ApiResponse<PaginatedApiResult<MessageGroup>, ApiError>> =>
    withJwt(mangoApi).get<PaginatedApiResult<MessageGroup>, ApiError>(
      '/app/v1/message-groups/',
      params,
    ),
  listPhoneNumbers: (
    params: SwitchboardListPhoneNumbersParams,
  ): Promise<ApiResponse<PaginatedApiResult<PhoneNumber>, ApiError>> =>
    withJwt(mangoApi).get<PaginatedApiResult<PhoneNumber>, ApiError>(
      '/app/v2/phone-numbers/',
      params,
    ),
  listSchedules: (params: {
    pbx_id: number
    limit?: number
    offset?: number
  }): Promise<ApiResponse<PaginatedApiResult<SwitchboardSchedule>, ApiError>> =>
    withJwt(mangoApi).get<PaginatedApiResult<SwitchboardSchedule>, ApiError>(
      '/app/v1/schedules/',
      params,
    ),
  listSoundClips: (params: {
    pbx_id: number
    limit?: number
    offset?: number
  }): Promise<
    ApiResponse<PaginatedApiResult<SwitchboardSoundClip>, ApiError>
  > =>
    withJwt(mangoApi).get<PaginatedApiResult<SwitchboardSoundClip>, ApiError>(
      '/app/v1/sound-clips/',
      params,
    ),
})
